// src/pages/shortStory/ShortStoryChatPage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAuthHeaders, urlMap, appNavigation } from '../../utils';
import TopBar from '../components/TopBar';
import '../../assets/styles/components/ShortStory.css';

async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    credentials: 'include',
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(payload?.error || `HTTP_${res.status}`), { body: payload });
  return payload;
}

export default function ShortStoryChatPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scrollRef = useRef(null);
  const autoStartAttemptedRef = useRef(false);
  const requestedScaffoldId = searchParams.get('scaffoldId');

  const [phase, setPhase] = useState('gallery'); // gallery | loading | chat | done | error
  const [scaffolds, setScaffolds] = useState([]);
  const [scaffoldsLoading, setScaffoldsLoading] = useState(true);
  const [scaffoldsError, setScaffoldsError] = useState(null);
  const [selectedScaffold, setSelectedScaffold] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [total, setTotal] = useState(10);
  const [submitting, setSubmitting] = useState(false);
  const [warnedBlank, setWarnedBlank] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  }, []);

  const loadScaffolds = useCallback(async () => {
    setScaffoldsLoading(true);
    setScaffoldsError(null);
    try {
      const result = await fetch(urlMap.listStoryScaffolds(), {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const payload = await result.json().catch(() => []);
      if (!result.ok) throw new Error(payload?.error || `HTTP_${result.status}`);
      setScaffolds(Array.isArray(payload) ? payload : []);
    } catch (error) {
      setScaffoldsError(error?.message || 'Could not load story scaffolds. Please try again.');
    } finally {
      setScaffoldsLoading(false);
    }
  }, []);

  const startSession = useCallback(async (scaffold) => {
    setSelectedScaffold(scaffold);
    setPhase('loading');
    try {
      const result = await postJson(urlMap.shortStoryStart(), { storyScaffoldId: scaffold.id, fresh: true });
      const initialMessages = result.previousMessages ?? [{ type: 'question', text: result.question }];

      if (result.done) {
        setMessages([
          ...initialMessages,
          {
            type: 'question',
            text: 'Great — I have everything I need. Click "Generate my draft" to bring your story to life.',
          },
        ]);
        setPhase('done');
        setSessionId(result.sessionId);
        setQuestionIndex(result.total);
        setTotal(result.total);
      } else {
        setMessages(initialMessages);
        setPhase('chat');
        setSessionId(result.sessionId);
        setQuestionIndex(result.questionIndex);
        setTotal(result.total);
      }
      scrollToBottom();
    } catch {
      setPhase('error');
      setErrorMessage('Could not start the questionnaire. Please try again.');
    }
  }, [scrollToBottom]);

  useEffect(() => {
    loadScaffolds();
  }, [loadScaffolds]);

  useEffect(() => {
    if (!requestedScaffoldId || autoStartAttemptedRef.current || scaffoldsLoading) return;

    const matched = scaffolds.find((item) => item.id === requestedScaffoldId);
    if (matched) {
      autoStartAttemptedRef.current = true;
      startSession(matched);
      return;
    }

    autoStartAttemptedRef.current = true;
    (async () => {
      try {
        const res = await fetch(urlMap.getAuthorStoryScaffold(requestedScaffoldId), {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
        });
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.error || `HTTP_${res.status}`);

        await startSession({
          id: payload.id,
          title: payload.title,
          description: payload.description,
          coverAssetUrl: payload.coverAssetUrl,
        });
      } catch {
        setPhase('error');
        setErrorMessage('Could not start the questionnaire for this scaffold. Please try another scaffold.');
      }
    })();
  }, [requestedScaffoldId, scaffoldsLoading, scaffolds, startSession]);

  const handleNext = async () => {
    const trimmed = currentAnswer.trim();

    if (!trimmed && !warnedBlank) {
      setWarnedBlank(true);
      setMessages((prev) => [
        ...prev,
        { type: 'question', text: 'No worries — even a quick "I don\'t know" helps. What comes to mind?' },
      ]);
      scrollToBottom();
      return;
    }

    const answer = trimmed || "I don't know";
    setSubmitting(true);
    setWarnedBlank(false);

    try {
      const result = await postJson(urlMap.shortStoryAnswer(), {
        sessionId,
        answer,
        questionIndex,
        storyScaffoldId: selectedScaffold?.id,
      });

      setMessages((prev) => {
        const next = [...prev, { type: 'answer', text: answer }];
        if (result.done) {
          next.push({
            type: 'question',
            text: 'Great — I have everything I need. Click "Generate my draft" to bring your story to life.',
          });
        } else {
          next.push({ type: 'question', text: result.nextQuestion });
        }
        return next;
      });

      setCurrentAnswer('');
      setSubmitting(false);
      if (result.done) {
        setPhase('done');
      } else {
        setQuestionIndex(result.nextQuestionIndex);
      }
      scrollToBottom();
    } catch {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!submitting) handleNext();
    }
  };

  const handleGenerate = () => {
    navigate(`${appNavigation.shortStoryGenerating}?sessionId=${encodeURIComponent(sessionId)}`);
  };

  const handleBackToGallery = () => {
    if (phase === 'gallery') {
      navigate(appNavigation.home);
      return;
    }

    setPhase('gallery');
    setMessages([]);
    setCurrentAnswer('');
    setSessionId(null);
    setQuestionIndex(0);
    setTotal(10);
    setSelectedScaffold(null);
    setSubmitting(false);
    setWarnedBlank(false);
    setErrorMessage(null);
  };

  const currentQNum = Math.min(questionIndex + 1, total);
  const progressLabel = phase === 'done' ? `${total} of ${total}` : `Question ${currentQNum} of ${total}`;
  const isLastQuestion = questionIndex === total - 1;

  return (
    <div className="home-page short-story-page">
      <TopBar />
      <main className="home-main short-story-main">
        <div className="short-story-panel">
          <div className="short-story-panel__header">
            <button type="button" className="short-story-back" onClick={handleBackToGallery}>
              {phase === 'gallery' ? '← Home' : '← Gallery'}
            </button>
            <h2 className="short-story-panel__title">
              {phase === 'gallery' ? 'Choose a story scaffold' : 'Short Story Questionnaire'}
            </h2>
          </div>

          {phase === 'gallery' && (
            <div className="short-story-gallery">
              <div className="short-story-gallery__intro">
                <p className="short-story-muted">Choose a narrative scaffold to give your story a starting shape.</p>
              </div>
              {scaffoldsLoading && (
                <div className="short-story-centered">
                  <div className="short-story-spinner" />
                  <p className="short-story-muted">Loading story scaffolds…</p>
                </div>
              )}
              {scaffoldsError && (
                <div className="short-story-centered">
                  <p className="short-story-error">{scaffoldsError}</p>
                  <button type="button" className="short-story-btn primary" onClick={loadScaffolds}>Try again</button>
                </div>
              )}
              {!scaffoldsLoading && !scaffoldsError && (
                <div className="short-story-gallery__grid">
                  {scaffolds.map((scaffold) => (
                    <button
                      type="button"
                      className="short-story-scaffold"
                      key={scaffold.id}
                      onClick={() => startSession(scaffold)}
                    >
                      {scaffold.coverAssetUrl ? (
                        <img src={scaffold.coverAssetUrl} alt="" className="short-story-scaffold__cover" />
                      ) : (
                        <div className="short-story-scaffold__cover short-story-scaffold__cover--placeholder" aria-hidden="true">✦</div>
                      )}
                      <span className="short-story-scaffold__title">{scaffold.title}</span>
                      <span className="short-story-scaffold__description">{scaffold.description}</span>
                      <span className="short-story-scaffold__start">Start with this scaffold →</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {phase === 'loading' && (
            <div className="short-story-centered">
              <div className="short-story-spinner" />
              <p className="short-story-muted">Starting your questionnaire…</p>
            </div>
          )}

          {phase === 'error' && (
            <div className="short-story-centered">
              <p className="short-story-error">{errorMessage}</p>
              <button type="button" className="short-story-btn primary" onClick={() => startSession(selectedScaffold)}>
                Try again
              </button>
            </div>
          )}

          {(phase === 'chat' || phase === 'done') && (
            <>
              <div className="short-story-progress">
                <span className="short-story-progress__label">{progressLabel}</span>
                <div className="short-story-progress__track">
                  <div
                    className="short-story-progress__fill"
                    style={{ width: `${(currentQNum / total) * 100}%` }}
                  />
                </div>
              </div>

              <div className="short-story-chat" ref={scrollRef}>
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    className={`short-story-bubble-row ${msg.type === 'question' ? 'left' : 'right'}`}
                  >
                    <div className={`short-story-bubble ${msg.type === 'question' ? 'question' : 'answer'}`}>
                      {msg.text}
                    </div>
                  </div>
                ))}
              </div>

              <div className="short-story-input-row">
                {phase === 'done' ? (
                  <button type="button" className="short-story-btn primary full" onClick={handleGenerate}>
                    Generate my draft
                  </button>
                ) : (
                  <>
                    <textarea
                      className="short-story-textarea"
                      value={currentAnswer}
                      onChange={(e) => {
                        setCurrentAnswer(e.target.value);
                        setWarnedBlank(false);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder="Your answer…"
                      disabled={submitting}
                      autoFocus
                      rows={2}
                    />
                    <button
                      type="button"
                      className="short-story-btn primary"
                      onClick={handleNext}
                      disabled={submitting}
                    >
                      {submitting ? '…' : isLastQuestion ? 'Done' : 'Next'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
